import type { JSX } from 'preact';
// Built by `npm run art` before the web build (see the root and CI scripts). The raw string
// is bundled into the JS, so it needs no separate PWA precache entry.
import spriteRaw from '../../../../packages/art/dist/sprite.svg?raw';
import {
  Apple,
  ArrowRightLeft,
  Award,
  Beef,
  Bird,
  Birdhouse,
  Book,
  BookOpen,
  Box,
  Bridge,
  Camera,
  Carrot,
  Check,
  ChevronLeft,
  Church,
  Circle,
  ClipboardList,
  CloudRain,
  Cog,
  Coins,
  Cookie,
  Croissant,
  Cylinder,
  DoorOpen,
  Droplet,
  Egg,
  Fence,
  FileText,
  Flame,
  Flower2,
  Fuel,
  Gem,
  Gift,
  Grape,
  Hammer,
  Home,
  Hourglass,
  IceCreamCone,
  LandPlot,
  Landmark,
  Leaf,
  Link2,
  Logs,
  Medal,
  Milk,
  Mountain,
  Move,
  Package,
  RefreshCw,
  Ribbon,
  Route,
  Rows3,
  Scissors,
  Settings,
  Shovel,
  Snowflake,
  Sparkles,
  SprayCan,
  Sprout,
  Square,
  SquareArrowUp,
  Star,
  Stethoscope,
  Store,
  Sun,
  Tent,
  Tractor,
  Trees,
  TreePine,
  Trophy,
  Truck,
  User,
  Users,
  UtilityPole,
  Warehouse,
  Wheat,
  Wind,
  Wine,
  Wrench,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-preact';

/**
 * Stand-in icon set from the design system: Lucide, 2px stroke at 24, currentColor.
 * Only the names the screens use are imported so the bundle stays small. When the
 * generated icon set in packages/art lands, this map points at the sprite instead.
 */
/** D457: the design system's lock (icons/lock.svg), drawn like a Lucide glyph. */
const Lock = (({ size = 24, strokeWidth = 2 }: { size?: number; strokeWidth?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width={strokeWidth}
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <rect x="5" y="10.5" width="14" height="10.5" rx="2.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    <path d="M12 14.5v2.5" />
  </svg>
)) as unknown as LucideIcon;

const ICONS: Record<string, LucideIcon> = {
  lock: Lock,
  apple: Apple,
  'arrow-right-left': ArrowRightLeft,
  'chevron-left': ChevronLeft,
  award: Award,
  beef: Beef,
  bird: Bird,
  birdhouse: Birdhouse,
  book: Book,
  'book-open': BookOpen,
  box: Box,
  bridge: Bridge,
  camera: Camera,
  carrot: Carrot,
  check: Check,
  church: Church,
  circle: Circle,
  'clipboard-list': ClipboardList,
  'cloud-rain': CloudRain,
  cog: Cog,
  coins: Coins,
  cookie: Cookie,
  croissant: Croissant,
  cylinder: Cylinder,
  'door-open': DoorOpen,
  droplet: Droplet,
  egg: Egg,
  fence: Fence,
  'file-text': FileText,
  flame: Flame,
  'flower-2': Flower2,
  fuel: Fuel,
  gem: Gem,
  gift: Gift,
  grape: Grape,
  hammer: Hammer,
  home: Home,
  hourglass: Hourglass,
  'ice-cream-cone': IceCreamCone,
  'land-plot': LandPlot,
  landmark: Landmark,
  leaf: Leaf,
  link: Link2,
  log: Logs,
  logs: Logs,
  medal: Medal,
  milk: Milk,
  mountain: Mountain,
  move: Move,
  package: Package,
  'refresh-cw': RefreshCw,
  ribbon: Ribbon,
  route: Route,
  'rows-3': Rows3,
  scissors: Scissors,
  settings: Settings,
  shovel: Shovel,
  snowflake: Snowflake,
  sparkles: Sparkles,
  'spray-can': SprayCan,
  sprout: Sprout,
  square: Square,
  'square-arrow-up': SquareArrowUp,
  star: Star,
  stethoscope: Stethoscope,
  store: Store,
  sun: Sun,
  tent: Tent,
  tractor: Tractor,
  trees: Trees,
  'tree-pine': TreePine,
  trophy: Trophy,
  truck: Truck,
  user: User,
  users: Users,
  'utility-pole': UtilityPole,
  warehouse: Warehouse,
  wheat: Wheat,
  wind: Wind,
  wine: Wine,
  wrench: Wrench,
  x: X,
  zap: Zap,
};

export const iconNames = Object.keys(ICONS);

/** Every `<symbol id="icon-{id}">` the generated set built, read once from the sprite text. */
const SPRITE_IDS = new Set(Array.from(spriteRaw.matchAll(/id="icon-([a-z0-9_]+)"/g), (m) => m[1]));

/** True when `packages/art` generated an icon for this name (a content id, not a UI glyph). */
export function hasSpriteIcon(name: string): boolean {
  return SPRITE_IDS.has(name);
}

/** The sprite markup, inlined into the document once at app start. See `main.tsx`. */
export { spriteRaw };

export interface IconProps {
  name: string;
  size?: number;
  stroke?: number;
  style?: JSX.CSSProperties;
  title?: string;
}

/**
 * A content icon renders from the generated sprite (`<use href="#icon-{name}">`, currentColor).
 * A UI glyph not in that set (check, chevrons, settings and so on) falls back to Lucide. An
 * unknown name renders an empty box of the same size.
 */
export function Icon({ name, size = 24, stroke = 2, style, title }: IconProps) {
  /* D259: a producer whose own icon has not been drawn yet shows the sprout. */
  if (name.startsWith('producer_') && !hasSpriteIcon(name)) name = 'sprout';
  const sprite = hasSpriteIcon(name);
  const Glyph = sprite ? undefined : ICONS[name];
  return (
    <span
      title={title}
      aria-hidden={title ? undefined : true}
      style={{
        display: 'inline-flex',
        width: size,
        height: size,
        flex: 'none',
        color: 'inherit',
        ...style,
      }}
    >
      {sprite ? (
        <svg width={size} height={size} aria-hidden focusable="false">
          <use href={`#icon-${name}`} />
        </svg>
      ) : Glyph ? (
        <Glyph size={size} strokeWidth={stroke} aria-hidden />
      ) : null}
    </span>
  );
}
